import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as ScrollLink, scrollSpy } from 'react-scroll';

import Layout from '../../Layout';
import Button from '../../shared/buttonComponents/Button';
import { useMediaQuery } from '../../../contexts/MediaQueryContext';
import SelectBox from '../../shared/select-boxes/SelectBox';
import { useWizard } from '../../../contexts/WizardContext';
import ProposalPopup from './ProposalPopup';
import SelectBoxProposal from '../../shared/select-boxes/SelectBoxProposal';
import AddSolutionsPopup from './AddSolutionsPopup';
import DynamicTable from './dynamic-table/DynamicTable';

export interface RegionsListItem {
  name: string;
  icon: string;
  gallery: any;
  selected: boolean;
}

const ProposalPage: FC = (): JSX.Element => {
  const {
    data,
    updateStepData,
    staticData
  } = useWizard();

  scrollSpy.update();

  const { onMobile } = useMediaQuery();

  const regionsList = useMemo((): RegionsListItem[] => {
    return staticData.solutions.map((solution) => ({
      name: solution.name,
      gallery: solution.gallery,
      icon: `regions/${solution.name.toLocaleLowerCase().replace(/\\s/g, '-')}`,
      selected: false
    }));
  }, [staticData.solutions]);

  const [proposalSelected, setProposalSelected] = useState<boolean>(false);
  const [regions, setRegions] = useState<RegionsListItem[]>(regionsList);

  useEffect(() => {
    if (!data.solutions) {
      return;
    }

    setRegions((state) => state.map((region) => {
      region.selected = data.solutions.includes(region.name);

      return region;
    }));
  }, [data.solutions]);

  const handleSelect = useCallback((name, selected, fromMobile = false) => {
    if (onMobile && !fromMobile) {
      return;
    }

    if (selected) {
      const data = regions
        .filter(r => r.selected)
        .map(r => r.name);

      data.push(name);

      updateStepData({
        solutions: data
      });
    } else {
      const data = regions
        .filter(r => r.selected && r.name !== name)
        .map(r => r.name);

      updateStepData({
        solutions: data
      });
    }
  }, [regions, onMobile, updateStepData]);

  const [termsNotRead, setTermsNotRead] = useState<boolean>(true);
  const [openPopup, setOpenPopup] = useState<boolean>(false);

  return (
    <Layout>
      <div className={onMobile ? 'proposal-page-mobile' : 'proposal-page'}>
        {openPopup && (
          <AddSolutionsPopup
            onClose={() => setOpenPopup(false)}
          />
        )}

        {proposalSelected && (
          <ProposalPopup onClose={() => setProposalSelected(false)}/>
        )}

        <div className='proposal-page-top'>
          <div className='proposal-page-titles'>
            <div className="secondary-title">
              <p className='desktop-title'>Here it is! The checker has found the following pricing <br/> within our
                banking network according to your selection</p>
            </div>

            <div className="main-title">
              <p className='desktop-title'>{onMobile ? 'THE PROPOSAL' : 'PROPOSAL'}</p>
            </div>
          </div>
          <div className='proposal-page-buttons'>
            <Button
              disabled={termsNotRead}
              onClick={() => setProposalSelected(true)}
              title="Next"
            />
            <div className='checkbox-section'>
              <input
                onChange={() => termsNotRead ? setTermsNotRead(false) : setTermsNotRead(true)}
                checked={!termsNotRead}
                type='checkbox'
              />
              <p>
                I have read the{' '}
                <a href='https://zotapay.com/terms-of-use/' target='_blank' rel='noopener noreferrer'>
                  conditions & notes
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className={onMobile ? 'proposal-page-container-mobile' : 'proposal-page-container'}>
          <div className="proposal-page-content">

            <div className='proposals-list'>
              <div className='proposal-item'>

                <div className='boxes'>
                  <div className="regions-page-list">
                    {regions
                      .filter(r => r.selected && r.name)
                      .map((region, index) => (
                        <ScrollLink
                          key={index}
                          activeClass='scroll'
                          to={region.name as string}
                          spy={true}
                          smooth={true}
                          duration={100}
                          className='item'
                          containerId='scroll-wrapper'
                        >
                          <SelectBoxProposal
                            multi={false}
                            isSelected={false}
                            icon={region.icon}
                            title={region.name}
                            removable={true}
                            onClickX={() => handleSelect(region.name, !region.selected, false)}
                          />
                        </ScrollLink>
                      ))}
                    <div className='add-more-btn'>
                      <SelectBox
                        multi={false}
                        isSelected={false}
                        icon='add-more'
                        title='Add More'
                        onClick={() => setOpenPopup(true)}
                      />
                    </div>
                  </div>
                </div>
                <DynamicTable regions={regions}/>
              </div>
              <div className='add-more-btn-mobile'>
                <SelectBox
                  multi={false}
                  isSelected={false}
                  icon='add-more'
                  title='Add More'
                  onClick={() => setOpenPopup(true)}
                >
                </SelectBox>
              </div>
            </div>
          </div>
        </div>

        <div className='proposal-page-buttons-mobile'>
          <Button
            disabled={termsNotRead}
            onClick={() => setProposalSelected(true)}
            title="Next"
          />
          <div className='checkbox-section'>
            <input
              onChange={() => termsNotRead ? setTermsNotRead(false) : setTermsNotRead(true)}
              checked={!termsNotRead}
              type='checkbox'
            />
            <p>
              I have read the{' '}
              <a href='https://zotapay.com/terms-of-use/' target='_blank' rel='noopener noreferrer'>
                conditions & notes
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className='proposal-page-buttons-bottom'>
        <Button
          disabled={termsNotRead}
          onClick={() => setProposalSelected(true)}
          title="Next"
        />
        <div className='checkbox-section'>
          <input
            onChange={() => termsNotRead ? setTermsNotRead(false) : setTermsNotRead(true)}
            checked={!termsNotRead}
            type='checkbox'/>
          <p>
            I have read the{' '}
            <a href='https://zotapay.com/terms-of-use/' target='_blank' rel='noopener noreferrer'>
              conditions & notes
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default ProposalPage;
